import { ref, onMounted, nextTick, type Ref } from 'vue';

/**
 * useHydrated - SSR/SSG hydration-safety flag.
 *
 * Returns a reactive boolean that is `false` during SSR/SSG pre-render AND
 * during the client's FIRST render, then flips to `true` only after the
 * initial mount has committed (`onMounted` + `nextTick`).
 *
 * Why: vite-ssg pre-renders the marketing routes to static HTML. For Vue to
 * *adopt* (hydrate) that DOM instead of discarding and re-rendering it, the
 * client's first render must be byte-identical to the server render. Any UI
 * that depends on browser-only state (locale preference, auth, platform) would
 * otherwise diverge on first render and force a full client re-render — which
 * replays every `motion-*` entrance animation (the double-animation stutter).
 *
 * Gate such UI behind this flag: render the SSR-equivalent default form while
 * `false`, then reactively reflect the real value once `true`. The post-mount
 * switch is a normal reactive patch, not a node replacement, so it does not
 * replay entrance animations.
 *
 * The flag is a module-level singleton: it flips once for the whole app after
 * the initial mount, so every consumer transitions in the same tick.
 *
 * @example
 * const isHydrated = useHydrated();
 * const showRealNav = computed(() => isHydrated.value && isAuthenticated.value);
 */
const isHydrated = ref(false);

export function useHydrated(): Ref<boolean> {
  onMounted(() => {
    // Wait for the initial render to commit before flipping, so the first
    // render stays identical to the SSR output and Vue hydrates in place.
    nextTick(() => {
      isHydrated.value = true;
    });
  });

  return isHydrated;
}
